import { useSurveyLibraryStore } from "../hooks";
import PreviewQuestionField from "./PreviewQuestionField";
import AppDialog from "@/components/common/AppDialog";
import { useTran } from "@/hooks";
import { useState, useEffect } from "react";
import { ChevronLeft, Globe, ChevronRight, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const SurveyPreviewModal = ({ open, onClose, survey }) => {
  const { t } = useTran();
  const store = useSurveyLibraryStore();

  // ✅ ALL hooks declared first — unconditionally
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  const surveyId = survey?._id; // ✅ optional chaining
  const questions = selectedQuestions;
  const total = questions.length;
  const question = questions[current];
  const progress = total > 0 ? Math.round(((current + 1) / total) * 100) : 0;
  const isLast = current >= total - 1;

  // ✅ useEffect also unconditional — guard inside
  useEffect(() => {
    if (!surveyId) return; // ← guard INSIDE effect, not outside

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setCurrent(0); // reset to Q1 on new survey
      try {
        const data = await store.fetchSurveyById(surveyId);
        if (!cancelled) {
          setSelectedQuestions(data?.questions || []);
        }
      } catch (err) {
        console.error("Failed to load survey:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [surveyId]);

  // ✅ early return AFTER all hooks
  if (!survey) return null;

  const handlePrev = () => setCurrent((p) => Math.max(0, p - 1));
  const handleNext = () => setCurrent((p) => Math.min(total - 1, p + 1));

  const handleOpenChange = (o) => {
    if (!o) {
      setCurrent(0);
      setSelectedQuestions([]);
      onClose();
    }
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={t("Survey Preview")}
      size="lg"
    >
      {/* Card Header */}
      <div
        className={cn(
          "flex items-center justify-between px-6 py-4 border-b",
          "border-gray-200 dark:border-white/10",
        )}
      >
        <span
          className="font-bold tracking-wide text-sm
                                 text-emerald-600 dark:text-emerald-400"
        >
          NXSTART
        </span>
        <div
          className="flex items-center gap-3 text-xs
                                text-gray-500 dark:text-gray-400"
        >
          <span className="flex items-center gap-1">
            <Globe className="h-3.5 w-3.5" /> English
          </span>
          <Sun className="h-3.5 w-3.5 dark:hidden" />
          <Moon className="h-3.5 w-3.5 hidden dark:block" />
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-6 pt-4 pb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span
            className="text-xs font-medium
                                     text-gray-600 dark:text-gray-300"
          >
            {survey.name || t("Survey Preview")}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {total > 0 ? `${current + 1} of ${total} (${progress}%)` : "—"}
          </span>
        </div>
        <div
          className="h-1 w-full rounded-full overflow-hidden
                                bg-gray-200 dark:bg-gray-700"
        >
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question Body */}
      <div className="px-6 py-6 space-y-4 min-h-[220px]">
        {loading ? (
          <div
            className="flex items-center justify-center h-[220px]
                                    text-gray-400 dark:text-gray-500 text-sm"
          >
            {t("Loading...")}
          </div>
        ) : question ? (
          <>
            <p
              className="text-base leading-snug
                                        dark:text-white"
            >
              {question.questionText || t("Untitled Question")}
            </p>
            <PreviewQuestionField question={question} />
          </>
        ) : (
          <div
            className="flex items-center justify-center h-[220px]
                                    text-gray-400 dark:text-gray-500 text-sm"
          >
            {t("No questions in this survey.")}
          </div>
        )}
      </div>

      {/* Navigation Footer */}
      <div
        className={cn(
          "flex items-center justify-between px-6 py-4 border-t",
          "border-gray-200 dark:border-white/10",
        )}
      >
        <Button
          onClick={handlePrev}
          disabled={current === 0 || loading}
          className="gap-1"
        >
          <ChevronLeft className="h-4 w-4" /> {t("Previous")}
        </Button>
        {/* ✅ Last question → Submit, otherwise → Next */}
        {isLast ? (
          <Button
            disabled={loading}
            className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {t("Submit")}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={loading}
            className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {t("Next")}
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </AppDialog>
  );
};

export default SurveyPreviewModal;
